import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, Calendar, Tag } from "lucide-react";
import { apiAuthGet, apiAuthSend } from "@/lib/api";
import Modal, { Field, Input, Textarea, Btn } from "./_ui";
import FileUpload from "./FileUpload";

const BLANK = {
  title: "", subtitle: "", tagline: "", price: "", price_note: "", savings: "",
  highlight: false, badge: "", icon: "💸",
  valid_from: "", valid_until: "", pdf_url: "",
  features: "", sort_order: 0,
};

const toForm = (a) => ({
  ...BLANK, ...a,
  features: Array.isArray(a.features) ? a.features.join("\n") : "",
});

const toBody = (f) => ({
  title: f.title.trim(),
  subtitle: f.subtitle || "",
  tagline: f.tagline || "",
  price: f.price.trim(),
  price_note: f.price_note || "",
  savings: f.savings || null,
  highlight: !!f.highlight,
  badge: f.badge || "",
  icon: f.icon || "💸",
  valid_from: f.valid_from,
  valid_until: f.valid_until,
  pdf_url: f.pdf_url.trim(),
  features: (f.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
  sort_order: Number(f.sort_order) || 0,
});

export default function AngeboteAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => apiAuthGet("/admin/angebote").then(setItems).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const openNew = () => { setEditing("new"); setForm(BLANK); };
  const openEdit = (a) => { setEditing(a.id); setForm(toForm(a)); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = toBody(form);
      if (editing === "new") await apiAuthSend("POST", "/admin/angebote", body);
      else await apiAuthSend("PUT", `/admin/angebote/${editing}`, body);
      setEditing(null); await load();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Angebot wirklich löschen?")) return;
    setBusy(true);
    try { await apiAuthSend("DELETE", `/admin/angebote/${id}`); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-angebote">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-1">Content</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-[Outfit]">TM7 Angebote</h1>
          <p className="text-slate-400 mt-1.5 text-sm">{items.length} {items.length === 1 ? "Angebot" : "Angebote"} · Sortierung über „Reihenfolge"</p>
        </div>
        <Btn onClick={openNew} data-testid="angebote-new"><Plus className="w-4 h-4" /> Neues Angebot</Btn>
      </div>

      {err && <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-300">{err}</div>}

      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((a) => (
          <div key={a.id} data-testid={`angebote-row-${a.id}`}
            className="rounded-2xl p-4 sm:p-5 bg-white/5 border border-white/10 hover:border-emerald-400/30 transition">
            <div className="flex items-start gap-3 mb-3">
              <div className="text-3xl shrink-0">{a.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {a.highlight && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Highlight</span>}
                  {a.badge && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-slate-300 border border-white/10">{a.badge}</span>}
                  <span className="text-[10px] text-slate-500 font-mono">#{a.sort_order}</span>
                </div>
                <h3 className="font-bold text-white font-[Outfit] mt-1 leading-tight break-words">{a.title}</h3>
                <p className="text-xs text-slate-400 mt-0.5 break-words">{a.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-3 flex-wrap">
              <Tag className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span className="text-white font-bold">{a.price} €</span>
              <span className="opacity-70 truncate">· {a.price_note}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
              <Calendar className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span>{a.valid_from} – {a.valid_until}</span>
            </div>
            <div className="flex gap-2">
              <Btn variant="secondary" className="flex-1" onClick={() => openEdit(a)} data-testid={`angebote-edit-${a.id}`}>
                <Pencil className="w-3.5 h-3.5" /> Bearbeiten
              </Btn>
              <Btn variant="danger" onClick={() => remove(a.id)} data-testid={`angebote-delete-${a.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Btn>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Neues Angebot" : "Angebot bearbeiten"} size="lg">
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="angebote-form">
          <Field label="Titel"><Input required value={form.title} onChange={onChange("title")} placeholder="Thermomix® TM7" /></Field>
          <Field label="Untertitel"><Input value={form.subtitle} onChange={onChange("subtitle")} placeholder="Mit 0 % Finanzierung" /></Field>
          <Field label="Tagline (kursiv)"><Input value={form.tagline} onChange={onChange("tagline")} /></Field>
          <Field label="Icon (Emoji)"><Input value={form.icon} onChange={onChange("icon")} placeholder="💸" /></Field>
          <Field label="Preis (ohne €)"><Input required value={form.price} onChange={onChange("price")} placeholder="1.549" /></Field>
          <Field label="Preis-Hinweis"><Input value={form.price_note} onChange={onChange("price_note")} /></Field>
          <Field label="Ersparnis (z.B. 133 €)"><Input value={form.savings || ""} onChange={onChange("savings")} /></Field>
          <Field label="Badge-Text"><Input value={form.badge} onChange={onChange("badge")} placeholder="TOP-DEAL" /></Field>
          <Field label="Gültig von" hint="Format: JJJJ-MM-TT"><Input required type="date" value={form.valid_from} onChange={onChange("valid_from")} /></Field>
          <Field label="Gültig bis"><Input required type="date" value={form.valid_until} onChange={onChange("valid_until")} /></Field>
          <Field label="Reihenfolge"><Input type="number" value={form.sort_order} onChange={onChange("sort_order")} /></Field>
          <div className="sm:col-span-2"><Field label="PDF (Flyer)" hint="PDF hochladen, oder URL manuell eingeben">
            <FileUpload value={form.pdf_url} onChange={(url) => setForm((f) => ({ ...f, pdf_url: url }))} testid="angebote-pdf" label="PDF hochladen" />
          </Field></div>
          <div className="sm:col-span-2"><Field label="Features (eine Zeile pro Feature)" hint={`${(form.features || "").split("\n").filter(Boolean).length} Features`}>
            <Textarea rows={5} value={form.features} onChange={onChange("features")} placeholder={"Thermomix® TM7 Komplett-Paket\nVarioma, Mixtopf …"} />
          </Field></div>
          <label className="sm:col-span-2 flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 cursor-pointer">
            <input type="checkbox" checked={form.highlight} onChange={onChange("highlight")} className="w-4 h-4 accent-emerald-500" />
            <span className="text-sm text-slate-200">Als Highlight (Top-Deal) anzeigen</span>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setEditing(null)}>Abbrechen</Btn>
            <Btn type="submit" disabled={busy} data-testid="angebote-save"><Save className="w-4 h-4" /> Speichern</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
